import React from "react";
import { ErrorDoodle } from "@/components/ErrorDoodle";
import * as Sentry from "@sentry/react";

interface NetworkInformation {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

export function ConnectionError() {
  React.useEffect(() => {
    const captureConnectionError = () => {
      const connectionInfo: Record<string, unknown> = {
        online: navigator.onLine,
        userAgent: navigator.userAgent,
        url: window.location.href,
        referrer: document.referrer,
        timestamp: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      // Try to get network information if available
      if ("connection" in navigator) {
        const nav = navigator as Navigator & { connection?: NetworkInformation };
        if (nav.connection) {
          connectionInfo.effectiveType = nav.connection.effectiveType;
          connectionInfo.downlink = nav.connection.downlink;
          connectionInfo.rtt = nav.connection.rtt;
          connectionInfo.saveData = nav.connection.saveData;
        }
      }

      // Check for common browser security features that might block requests
      connectionInfo.hasContentSecurityPolicy = "securityPolicy" in document;
      connectionInfo.hasCrossOriginIsolated = "crossOriginIsolated" in window;

      Sentry.captureMessage("Connection Error Page Displayed", {
        level: "error",
        tags: {
          errorType: "connection_error",
        },
        extra: connectionInfo,
      });
    };

    captureConnectionError();
  }, []);

  return (
    <div className="flex min-h-dvh w-full flex-col items-center justify-center gap-6 bg-background px-4 py-10 text-foreground">
      <div className="flex flex-col items-center gap-4">
        <ErrorDoodle />
        <h1 className="text-center text-3xl font-title tracking-tight md:text-[32px]">
          Connection Error
        </h1>
      </div>
      <div className="max-w-2xl space-y-4 text-center font-label text-muted-foreground">
        <p>We're unable to connect to the server. This could be due to:</p>
        <ul className="list-inside list-disc space-y-2 text-start">
          <li>No internet connection or unstable network</li>
          <li>Server is temporarily down or under maintenance</li>
          <li>Your request timed out</li>
          <li>DNS resolution issues</li>
          <li>Firewall or security software blocking the connection</li>
          <li>Proxy or VPN configuration issues</li>
        </ul>
        <p className="mt-4">
          Please check your connection and try again. If the problem persists, contact support.
        </p>
      </div>
    </div>
  );
}
